import { CONFIG } from '../config/environment';

const cache = new Map<string, { label: string; kind: string; title: string | null }>();

const URL_REGEX = /https?:\/\/[^\s<>")\]]+/g;
const BARE_VIDEO_ID = /(?<![a-zA-Z0-9_-])([a-zA-Z0-9_-]{11})(?![a-zA-Z0-9_-])/g;

function looksResolvable(token: string): boolean {
  return /youtube\.com|youtu\.be|instagram\.com|tiktok\.com|twitter\.com|x\.com|facebook\.com|linkedin\.com/.test(token)
    || /^[a-zA-Z0-9_-]{11}$/.test(token);
}

export function extractResolvableTokens(text: string): string[] {
  const tokens = new Set<string>();
  let m: RegExpExecArray | null;
  const urlRe = new RegExp(URL_REGEX.source, 'g');
  while ((m = urlRe.exec(text)) !== null) {
    const url = m[0].replace(/[.,;:!?)]+$/, '');
    if (looksResolvable(url)) tokens.add(url);
  }
  const idRe = new RegExp(BARE_VIDEO_ID.source, 'g');
  while ((m = idRe.exec(text)) !== null) {
    const id = m[1];
    if (!/^\d{11}$/.test(id)) tokens.add(id);
  }
  return Array.from(tokens);
}

export async function resolveLabels(tokens: string[]): Promise<Record<string, { label: string; kind: string; title: string | null }>> {
  const out: Record<string, { label: string; kind: string; title: string | null }> = {};
  const need: string[] = [];
  for (const t of tokens) {
    if (cache.has(t)) out[t] = cache.get(t)!;
    else need.push(t);
  }
  if (need.length === 0) return out;

  try {
    const response = await fetch(`${CONFIG.API_BASE_URL}/api/labels/resolve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ urls: need })
    });
    const data = await response.json();
    if (data?.results) {
      for (const [k, v] of Object.entries(data.results as Record<string, any>)) {
        cache.set(k, v);
        out[k] = v;
      }
    }
  } catch (_) {
    // ignore — leave raw tokens unresolved
  }
  return out;
}

export function applyLabels(text: string, labels: Record<string, { label: string; kind: string; title: string | null }>): string {
  let out = text;
  const sorted = Object.keys(labels).sort((a, b) => b.length - a.length);
  for (const token of sorted) {
    const meta = labels[token];
    if (!meta || !meta.label) continue;
    if (meta.label === token) continue;
    if (meta.kind === 'youtube' && !meta.title) continue;
    const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    out = out.replace(new RegExp(escaped, 'g'), meta.label);
  }
  return out;
}

export async function postprocessAssistantText(text: string): Promise<string> {
  const tokens = extractResolvableTokens(text);
  if (tokens.length === 0) return text;
  const labels = await resolveLabels(tokens);
  return applyLabels(text, labels);
}
